/**
 * Runtime white-label configuration reader.
 *
 * The config is loaded synchronously from whitelabel.json in index.html
 * and stored in window.__WHITELABEL__ before any module code executes.
 *
 * To customize branding:
 *   1. Edit ui/public/whitelabel.json (build-time)
 *   2. OR mount a custom file at /app/dist/control-ui/whitelabel.json (runtime)
 */

export interface WhiteLabelConfig {
  appName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  theme?: string;
  themeMode?: string;
  assistantName?: string;
  assistantAvatar?: string;
  loginSubtitle?: string;
  customCss?: string;
  poweredBy?: boolean;
  welcomeMessage?: string;
  footerText?: string;
}

declare global {
  interface Window {
    __WHITELABEL__?: WhiteLabelConfig;
  }
}

function raw(): WhiteLabelConfig {
  return (typeof window !== "undefined" && window.__WHITELABEL__) || {};
}

/** Application name shown in title, sidebar, login, breadcrumbs. */
export function wlAppName(): string {
  return raw().appName || "OpenClaw";
}

/** Logo URL — returns custom URL or falls back to the built-in favicon.svg. */
export function wlLogoUrl(basePath: string): string {
  const url = raw().logoUrl;
  if (url) return url;
  const base = basePath?.replace(/\/$/, "") ?? "";
  return base ? `${base}/favicon.svg` : "favicon.svg";
}

/** Default assistant display name. */
export function wlAssistantName(): string {
  return raw().assistantName || "Assistant";
}

/** Default assistant avatar letter/emoji. */
export function wlAssistantAvatar(): string {
  return raw().assistantAvatar || "A";
}

/** Full config object for advanced usage. */
export function wlConfig(): WhiteLabelConfig {
  return raw();
}
